import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener
} from '@angular/core';
import { Subject, timer, of, EMPTY } from 'rxjs';
import { takeUntil, switchMap, takeWhile, catchError, delay, retry, retryWhen, delayWhen } from 'rxjs/operators';
import { PaymentService, PaymentStatusResponse, PaymentType } from '@app/services/api/payment.service';
import { ModulePackagesService } from '@app/services/api/module-packages.service';
import { ProductPackagesService } from '@app/services/api/product-packages.service';

declare var Stripe: any;

// Stripe.js SRI hash - update when Stripe version changes
// Note: Stripe recommends not using SRI as they frequently update their script
// Instead, we rely on HTTPS + domain verification + CSP
const STRIPE_JS_URL = 'https://js.stripe.com/v3/';
const STRIPE_INIT_MAX_RETRIES = 50; // 50 * 100ms = 5 seconds max wait
const STRIPE_INIT_RETRY_INTERVAL_MS = 100;

@Component({
  selector: 'app-payment-authorization-modal',
  templateUrl: './payment-authorization-modal.component.html',
  standalone: false
})
export class PaymentAuthorizationModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() stripeClientSecret!: string;
  @Input() stripePublishableKey!: string;
  @Input() paymentType: PaymentType = 'module_package';
  @Input() ref?: string;
  @Input() skipPolling: boolean = false; // Skip status polling (for registration flow)
  /**
   * When true, render the card form inline (no `fixed inset-0` overlay, no
   * close header). Use this when the component is mounted inside another
   * overlay (e.g. MatDialog) whose transform creates a stacking context that
   * clips `position: fixed` children — symptom: error banner not visible.
   */
  @Input() embedded: boolean = false;
  @Output() paymentComplete = new EventEmitter<PaymentStatusResponse>();
  @Output() paymentCancelled = new EventEmitter<void>();
  @Output() paymentFailed = new EventEmitter<string>(); // Emitted when SetupIntent needs to be recreated
  /**
   * Emitted whenever Stripe rejects the card (declines, validation errors, etc.).
   * Lets the parent surface the error in its own UI — useful when this modal is
   * embedded inside another overlay (e.g. MatDialog) where the modal's own
   * fixed-position error banner can be clipped by the parent's stacking context.
   */
  @Output() cardSetupError = new EventEmitter<{ code: string | null; declineCode: string | null; message: string }>();

  @ViewChild('cardNumber') cardNumberRef!: ElementRef;
  @ViewChild('cardExpiry') cardExpiryRef!: ElementRef;
  @ViewChild('cardCvc') cardCvcRef!: ElementRef;

  public loading = true;
  public processing = false;
  public errorMessage: string | null = null;
  public configurationError = false;

  private stripe: any;
  private elements: any;
  private cardNumberElement: any;
  private cardExpiryElement: any;
  private cardCvcElement: any;
  private cardChangeListeners: Array<() => void> = [];
  private destroy$ = new Subject<void>();
  private isPolling = false;
  private stripeInitRetries = 0;
  private stripeInitTimeout?: ReturnType<typeof setTimeout>;
  private pollingTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private ngZone: NgZone,
    private paymentService: PaymentService,
    private modulePackagesService: ModulePackagesService,
    private productPackagesService: ProductPackagesService
  ) {}

  ngOnInit(): void {
    // Validate required inputs
    if (!this.stripeClientSecret || !this.stripePublishableKey) {
      this.errorMessage = 'Payment configuration is missing. Please try again or contact support.';
      this.configurationError = true;
      this.loading = false;
      return;
    }

    // Load Stripe.js dynamically
    this.loadStripeScript();
  }

  private loadStripeScript(): void {
    if (typeof Stripe !== 'undefined') {
      this.initializeStripe();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener('load', () => this.initializeStripe());
      existingScript.addEventListener('error', () => {
        this.errorMessage = 'Unable to load payment system. Please check your internet connection and try again.';
        this.loading = false;
      });
      return;
    }

    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.async = true;
    // Note: Stripe does not recommend SRI as they frequently update their script
    // Security is ensured via HTTPS, domain verification, and CSP headers
    script.onload = () => this.initializeStripe();
    script.onerror = () => {
      this.errorMessage = 'Unable to load payment system. Please check your internet connection and try again.';
      this.loading = false;
    };
    document.head.appendChild(script);
  }

  private initializeStripe(): void {
    try {
      this.stripe = Stripe(this.stripePublishableKey);
    } catch (error) {
      console.error('Failed to initialize Stripe:', error);
      this.errorMessage = 'Unable to initialize payment system. Please refresh the page.';
      this.loading = false;
    }
  }

  ngAfterViewInit(): void {
    if (this.configurationError) return;

    // Wait for Stripe to be ready with retry limit
    this.waitForStripeAndInitialize();
  }

  private waitForStripeAndInitialize(): void {
    if (this.stripe) {
      this.initializeStripeElements();
      return;
    }

    if (this.errorMessage) {
      // Error already occurred, don't retry
      return;
    }

    this.stripeInitRetries++;

    if (this.stripeInitRetries >= STRIPE_INIT_MAX_RETRIES) {
      this.errorMessage = 'Payment system is taking too long to load. Please refresh the page and try again.';
      this.loading = false;
      return;
    }

    this.stripeInitTimeout = setTimeout(() => this.waitForStripeAndInitialize(), STRIPE_INIT_RETRY_INTERVAL_MS);
  }

  private initializeStripeElements(): void {
    if (!this.stripe) {
      this.errorMessage = 'Payment system is not available. Please refresh the page.';
      this.loading = false;
      return;
    }

    try {
      this.elements = this.stripe.elements();

      const style = {
        base: {
          fontSize: '16px',
          color: '#32325d',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          '::placeholder': { color: '#aab7c4' }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      };

      this.cardNumberElement = this.elements.create('cardNumber', { style });
      this.cardExpiryElement = this.elements.create('cardExpiry', { style });
      this.cardCvcElement = this.elements.create('cardCvc', { style });

      // Mount elements with error handling
      this.mountElement(this.cardNumberElement, this.cardNumberRef, 'card number');
      this.mountElement(this.cardExpiryElement, this.cardExpiryRef, 'expiry date');
      this.mountElement(this.cardCvcElement, this.cardCvcRef, 'CVC');

      // Track ready event listener — runs outside Angular zone.
      // Safety net: when this component is embedded inside another overlay
      // (MatDialog), Stripe's `ready` event has occasionally been observed
      // not to fire, leaving the loading shade pinned over the card field.
      // Force-clear loading after 4s so the user can always interact.
      const readyHandler = () => {
        this.ngZone.run(() => { this.loading = false; });
      };
      this.cardNumberElement.on('ready', readyHandler);
      setTimeout(() => {
        if (this.loading) {
          this.ngZone.run(() => { this.loading = false; });
        }
      }, 4000);

      // Track change event listeners for cleanup — runs outside Angular zone
      const changeHandler = (event: any) => {
        this.ngZone.run(() => { this.errorMessage = event.error ? event.error.message : null; });
      };

      [this.cardNumberElement, this.cardExpiryElement, this.cardCvcElement].forEach((el) => {
        el.on('change', changeHandler);
        // Store cleanup functions
        this.cardChangeListeners.push(() => el.off('change', changeHandler));
      });
    } catch (error) {
      console.error('Failed to create Stripe elements:', error);
      this.errorMessage = 'Unable to load payment form. Please refresh the page.';
      this.loading = false;
    }
  }

  private mountElement(element: any, ref: ElementRef, name: string): void {
    try {
      if (!ref?.nativeElement) {
        throw new Error(`${name} container not found`);
      }
      element.mount(ref.nativeElement);
    } catch (error) {
      console.error(`Failed to mount ${name} element:`, error);
      this.errorMessage = `Unable to load payment form. Please refresh the page.`;
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    // Signal completion to all observables
    this.destroy$.next();
    this.destroy$.complete();

    // Stop polling
    this.isPolling = false;

    // Clear Stripe initialization timeout
    if (this.stripeInitTimeout) {
      clearTimeout(this.stripeInitTimeout);
      this.stripeInitTimeout = undefined;
    }

    // Clear all polling timeouts to prevent race conditions
    this.pollingTimeouts.forEach(timeoutId => {
      try {
        clearTimeout(timeoutId);
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    this.pollingTimeouts = [];

    // Remove all tracked event listeners before destroying elements
    this.cardChangeListeners.forEach(cleanup => {
      try {
        cleanup();
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    this.cardChangeListeners = [];

    // Unmount and destroy Stripe elements
    if (this.cardNumberElement) {
      try {
        this.cardNumberElement.unmount();
        this.cardNumberElement.destroy();
      } catch (e) {
        // Element may already be unmounted
      }
      this.cardNumberElement = null;
    }

    if (this.cardExpiryElement) {
      try {
        this.cardExpiryElement.unmount();
        this.cardExpiryElement.destroy();
      } catch (e) {
        // Element may already be unmounted
      }
      this.cardExpiryElement = null;
    }

    if (this.cardCvcElement) {
      try {
        this.cardCvcElement.unmount();
        this.cardCvcElement.destroy();
      } catch (e) {
        // Element may already be unmounted
      }
      this.cardCvcElement = null;
    }

    // Clear references
    this.elements = null;
    this.stripe = null;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (!this.processing) {
      this.onCancel();
    }
  }

  async onSubmit(): Promise<void> {
    if (!this.stripe || !this.cardNumberElement) {
      this.errorMessage = 'Payment form is not ready. Please wait a moment and try again.';
      return;
    }

    this.processing = true;
    this.errorMessage = null;

    try {
      const { setupIntent, error } = await this.stripe.confirmCardSetup(
        this.stripeClientSecret,
        {
          payment_method: {
            card: this.cardNumberElement
          }
        }
      );

      // Stripe resolves outside Angular zone — re-enter for state updates
      this.ngZone.run(() => {
        if (error) {
          // Always notify parents — they may need to show the message in
          // their own UI when our internal banner is hidden by an overlay
          // stacking context (e.g. when embedded in a MatDialog).
          this.cardSetupError.emit({
            code: error.code ?? null,
            declineCode: error.decline_code ?? null,
            message: error.message ?? '',
          });

          if (this.isTerminalError(error)) {
            this.processing = false;
            this.paymentFailed.emit(this.getHumanReadableError(error.message));
            return;
          }

          // Don't wipe the Stripe Elements on a soft decline — the iframes
          // visually "reload" and the user has to retype the whole card. They
          // can correct one digit if we leave the values in place.
          this.errorMessage = this.getHumanReadableError(error.message);
          this.processing = false;
        } else if (setupIntent && setupIntent.status === 'succeeded') {
          if (this.skipPolling) {
            this.processing = false;
            this.onPaymentSuccess({ status: 'activated', setup_intent_id: setupIntent.id } as PaymentStatusResponse);
            return;
          }
          this.pollActivationStatus(setupIntent.id);
        } else if (setupIntent && setupIntent.status === 'requires_action') {
          console.warn('SetupIntent requires additional action:', setupIntent.status);
          this.errorMessage = 'Additional authentication required. Please try again.';
          this.processing = false;
        } else {
          console.warn('Unexpected SetupIntent status:', setupIntent?.status);
          this.errorMessage = 'Payment authorization failed. Please try again.';
          this.processing = false;
        }
      });
    } catch (err: any) {
      this.ngZone.run(() => {
        console.error('Error confirming card setup:', err);
        this.errorMessage = 'An unexpected error occurred. Please try again.';
        this.processing = false;
      });
    }
  }

  /**
   * Check if the error is terminal (SetupIntent cannot be retried)
   */
  private isTerminalError(error: any): boolean {
    const terminalErrorCodes = [
      'setup_intent_unexpected_state',
      'setup_intent_authentication_failure',
      'setup_intent_mandate_invalid',
      'resource_missing', // SetupIntent was deleted/expired
    ];

    // Check error code
    if (error.code && terminalErrorCodes.includes(error.code)) {
      return true;
    }

    // Check if SetupIntent is in a terminal state based on error message
    if (error.message?.includes('SetupIntent') &&
        (error.message.includes('cannot be confirmed') ||
         error.message.includes('has already been') ||
         error.message.includes('unexpected state'))) {
      return true;
    }

    return false;
  }

  /**
   * Clear card elements for retry
   */
  private clearCardElements(): void {
    try {
      if (this.cardNumberElement) {
        this.cardNumberElement.clear();
      }
      if (this.cardExpiryElement) {
        this.cardExpiryElement.clear();
      }
      if (this.cardCvcElement) {
        this.cardCvcElement.clear();
      }
    } catch (e) {
      console.error('Error clearing card elements:', e);
    }
  }

  private getHumanReadableError(message: string): string {
    // Map technical errors to user-friendly messages
    const errorMappings: Record<string, string> = {
      'Your card was declined': 'Your card was declined. Please try a different card.',
      'Your card has insufficient funds': 'Your card has insufficient funds. Please try a different card.',
      'Your card has expired': 'Your card has expired. Please use a different card.',
      'Your card\'s security code is incorrect': 'The security code is incorrect. Please check and try again.',
    };

    for (const [key, value] of Object.entries(errorMappings)) {
      if (message.includes(key)) {
        return value;
      }
    }

    return message;
  }

  private pollActivationStatus(setupIntentId: string): void {
    if (this.isPolling) return;
    this.isPolling = true;

    const maxAttempts = 20;
    const maxConsecutiveErrors = 3;
    let attempts = 0;
    let consecutiveErrors = 0;

    // Calculate delay with exponential backoff: 1s, 1.5s, 2.25s, 3.375s... max 10s
    const getDelay = (attempt: number): number => {
      const baseDelay = 1000;
      const maxDelay = 10000;
      const delay = baseDelay * Math.pow(1.5, Math.min(attempt, 10));
      return Math.min(delay, maxDelay);
    };

    const poll = (): void => {
      if (!this.isPolling || attempts >= maxAttempts) {
        if (this.isPolling && attempts >= maxAttempts) {
          this.isPolling = false;
          this.errorMessage = 'Activation is taking longer than expected. Please check your packages page or contact support.';
          this.processing = false;
        }
        return;
      }

      attempts++;

      // Use unified PaymentService for status checks
      const statusRequest = {
        type: this.paymentType,
        setup_intent_id: setupIntentId,
        ...(this.ref && { ref: this.ref })
      };

      this.paymentService.checkStatus(statusRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: PaymentStatusResponse) => {
            consecutiveErrors = 0; // Reset on success

            if (response.status === 'activated') {
              this.isPolling = false;
              this.onPaymentSuccess(response);
            } else if (response.status === 'failed') {
              this.isPolling = false;
              this.errorMessage = response.message || 'Activation failed. Please try again or contact support.';
              this.processing = false;
            } else if (response.status === 'processing' || response.status === 'pending') {
              // Continue polling with backoff - track timeout for cleanup
              const timeoutId = setTimeout(() => poll(), getDelay(attempts));
              this.pollingTimeouts.push(timeoutId);
            } else {
              this.isPolling = false;
              this.errorMessage = 'Unexpected activation status. Please check your packages page.';
              this.processing = false;
            }
          },
          error: (error) => {
            console.error('Error checking activation status:', error);
            consecutiveErrors++;

            // Stop on too many consecutive errors (likely permanent issue)
            if (consecutiveErrors >= maxConsecutiveErrors) {
              this.isPolling = false;
              this.errorMessage = 'Unable to verify activation status. Please check your packages page or contact support.';
              this.processing = false;
              return;
            }

            // Retry with longer backoff on error - track timeout for cleanup
            const timeoutId = setTimeout(() => poll(), getDelay(attempts) * 2);
            this.pollingTimeouts.push(timeoutId);
          }
        });
    };

    // Start polling immediately
    poll();
  }

  onPaymentSuccess(response?: PaymentStatusResponse): void {
    // Clear the appropriate cache based on payment type
    if (this.paymentType === 'module_package') {
      this.modulePackagesService.clearCache();
    } else if (this.paymentType === 'product_package') {
      this.productPackagesService.clearCache();
    }
    this.paymentComplete.emit(response || {} as PaymentStatusResponse);
  }

  onCancel(): void {
    this.isPolling = false;
    this.paymentCancelled.emit();
  }
}
